import type { ComponentProps, CSSProperties, ReactNode } from 'react';
import { useId } from 'react';

import type { Size, UpdateStatus } from './types';
import { cn } from '../cn';
import WaveSpinner from '../WaveSpinner/WaveSpinner';
import FilterPopover from './FilterPopover';
import StageIcon, { isLightColor } from './StageIcon';
import Tooltip from './Tooltip';
import { cleanId, sizeFont, sizeGap, sizeIconClass, sizePaddingR } from './types';

export type Stage = {
  id: string;
  name: string | null;
  color: string | null;
  order: number | null;
};

export type StageButtonProps = Omit<ComponentProps<'div'>, 'onChange'> & {
  stages: Stage[] | undefined;
  stageId: string | undefined;
  onChange: (stageId: string | null) => void;
  disabled?: boolean;
  size?: Size;
  className?: string;
  /** Icon to show in expanded tooltip (between number and name) */
  icon?: ReactNode;
  /** Status for async operations: 'updating' shows spinner overlay. */
  status?: UpdateStatus;
};

// Square size classes matching height (outer container)
function sizeSquare(size: Size = 'md') {
  return {
    '3xs': 'size-4',
    '2xs': 'size-5',
    xs: 'size-6',
    sm: 'size-8',
    md: 'size-9',
    lg: 'size-10',
    xl: 'size-12',
    '2xl': 'size-16',
    '3xl': 'size-20',
    '4xl': 'size-24',
  }[size];
}

// Smaller circle for the colored stage indicator
function sizeCircle(size: Size = 'md') {
  return {
    '3xs': 'size-3.5',
    '2xs': 'size-4',
    xs: 'size-5',
    sm: 'size-6',
    md: 'size-7',
    lg: 'size-8',
    xl: 'size-10',
    '2xl': 'size-14',
    '3xl': 'size-16',
    '4xl': 'size-20',
  }[size];
}


// Smaller height for the expanded hover tooltip tag
function sizeExpandHeight(size: Size = 'md') {
  return {
    '3xs': 'h-3',
    '2xs': 'h-4',
    xs: 'h-5',
    sm: 'h-6',
    md: 'h-7',
    lg: 'h-8',
    xl: 'h-10',
    '2xl': 'h-14',
    '3xl': 'h-16',
    '4xl': 'h-20',
  }[size];
}

// Smaller font for the expanded hover tooltip
function sizeExpandFont(size: Size = 'md') {
  return {
    '3xs': 'text-[7px]',
    '2xs': 'text-[8px]',
    xs: 'text-[9px]',
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-xs',
    xl: 'text-sm',
    '2xl': 'text-base',
    '3xl': 'text-lg',
    '4xl': 'text-xl',
  }[size];
}

// Dropdown icon size — slightly bigger than default sizeIconClass for readability
function sizeDropdownIcon(size: Size = 'md') {
  return {
    '3xs': 'size-3',
    '2xs': 'size-3.5',
    xs: 'size-4',
    sm: 'size-5',
    md: 'size-6',
    lg: 'size-7',
    xl: 'size-8',
    '2xl': 'size-10',
    '3xl': 'size-12',
    '4xl': 'size-14',
  }[size];
}

// Expand-left style using CSS anchor positioning
function stageExpandStyle(anchorName: string, color: string | null): CSSProperties {
  return {
    position: 'fixed',
    positionAnchor: anchorName,
    positionArea: 'center',
    positionTryFallbacks: 'flip-block',
    justifySelf: 'end',
    margin: 0,
    inset: 'auto',
    zIndex: 50,
    background: color ?? undefined,
  } as CSSProperties;
}

export default function StageButton({
  stages,
  stageId,
  onChange,
  disabled,
  size = 'sm',
  className,
  icon,
  status,
  ...props
}: StageButtonProps) {
  const id = useId();
  const anchorName = `--stagebutton-${cleanId(id)}`;
  const popoverId = `stagebutton-popover-${cleanId(id)}`;

  const selectedStage = stages?.find((s) => s.id === stageId);

  const items = [
    // No shortcut for "Remove from Stage"
    ...(stageId ? [{ label: 'Remove from Stage', value: '' }] : []),
    // Pass order as shortcut for stages (only 1-9)
    ...(stages ?? []).map((s) => ({
      label: s.name || '',
      value: s.id,
      icon: <StageIcon color={s.color} order={s.order} size={size} />,
      shortcut: s.order && s.order >= 1 && s.order <= 9 ? s.order : undefined,
    })),
  ];

  return (
    <div className={cn('flex items-center justify-center', sizeSquare(size), className)} {...props}>
      <button
        type="button"
        disabled={disabled}
        popoverTarget={popoverId}
        style={{ anchorName } as CSSProperties}
        className={cn(
          'group/btn relative flex items-center justify-center rounded-full cursor-pointer outline-hidden',
          sizeSquare(size),
          disabled && 'opacity-50 cursor-not-allowed',
          selectedStage ? (isLightColor(selectedStage.color) ? 'text-black/70' : 'text-white') : 'text-slate-500 dark:text-slate-400',
        )}>
        {/* Circle with stage color */}
        {selectedStage && (
          <div
            className={cn('rounded-full flex items-center justify-center font-bold transition-opacity', sizeCircle(size), sizeFont(size), status === 'updating' && 'opacity-40')}
            style={{ background: selectedStage.color ?? undefined }}>
            <span className={cn('flex items-center justify-center rounded-full size-full', isLightColor(selectedStage.color) ? 'bg-black/10' : 'bg-black/20')}>
              {selectedStage.order}
            </span>
          </div>
        )}
        {!selectedStage && (
          <Tooltip
            tooltip="Pipeline stage — click to assign this contact to a stage"
            tooltipMaxWidth={240}
            className={cn(
              'rounded-full flex items-center justify-center border border-current transition-colors',
              'group-hover/btn:bg-slate-200 dark:group-hover/btn:bg-slate-700',
              sizeCircle(size),
              sizeFont(size),
              status === 'updating' && 'opacity-40',
            )}>
            S
          </Tooltip>
        )}
        {/* Loading spinner overlay */}
        {status === 'updating' && (
          <div className={cn('absolute inset-0 flex items-center justify-center', sizeSquare(size))}>
            <WaveSpinner className="size-1/2" />
          </div>
        )}

        {selectedStage?.name && (
          <span
            role="tooltip"
            style={stageExpandStyle(anchorName, selectedStage.color)}
            className={cn(
              'overflow-hidden',
              cn('flex items-center rounded-full uppercase tracking-wider font-normal pointer-events-none', isLightColor(selectedStage.color) ? 'text-black/70' : 'text-white'),
              'max-w-0 opacity-0 transition-all duration-200',
              'group-hover/btn:max-w-64 group-hover/btn:opacity-100',
              sizeExpandHeight(size),
              sizeExpandFont(size),
              sizeGap(size),
            )}>
            <span
              className={cn(
                'shrink-0 flex items-center justify-center rounded-full font-bold',
                isLightColor(selectedStage.color) ? 'bg-black/10' : 'bg-black/20',
                sizeExpandHeight(size),
                'aspect-square',
                sizeExpandFont(size),
              )}>
              {selectedStage.order}
            </span>
            {icon && (
              <span className={cn('shrink-0 flex items-center justify-center opacity-70', sizeIconClass(size))}>
                {icon}
              </span>
            )}
            <span className={cn('whitespace-nowrap', sizePaddingR(size))}>{selectedStage.name}</span>
          </span>
        )}
      </button>
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={size}
        items={items}
        selectedValue={stageId}
        onSelect={(val) => onChange(val || null)}
        filterable={false}
        iconClassName={cn(sizeDropdownIcon(size), '*:size-full')}
      />
    </div>
  );
}
